import { useNavigate } from "react-router-dom";
import { ReactNode, useEffect, useState, useCallback } from "react";
import { DocumentNode, gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { Maybe, ResponsePageInfo, Scalars } from "../../../gql/graphql";
import { DataTable as MantineDataTable } from "mantine-datatable";
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Select,
  Tooltip,
  Box,
  Text,
  Stack,
  Skeleton,
  Paper,
  ThemeIcon,
  Checkbox,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import { DataTableColumn } from "mantine-datatable";
import {
  IconArrowRight,
  IconArrowLeft,
  IconChevronRight,
  IconCopy,
  IconInfoCircle,
  IconTrash,
  IconPlus,
  IconInbox,
  IconAlertTriangle,
  IconRefresh,
  IconCheck,
} from "@tabler/icons-react";
import { DataTableProps } from "mantine-datatable";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { TadaDocumentNode } from "gql.tada";

const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.md,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing.sm,
  },
  tableWrapper: {
    backgroundColor: theme.colors.dark[7],
    borderRadius: theme.radius.lg,
    border: `1px solid ${theme.colors.dark[5]}`,
    overflow: "hidden",
  },
  expandIcon: {
    transition: "transform 0.2s ease",
  },
  expandIconRotated: {
    transform: "rotate(90deg)",
  },
  row: {
    "&:hover": {
      backgroundColor: `${theme.colors.dark[6]} !important`,
    },
  },
  selectedRow: {
    backgroundColor: `${theme.colors.dark[6]} !important`,
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing.md,
    borderTop: `1px solid ${theme.colors.dark[5]}`,
    backgroundColor: theme.colors.dark[8],
    flexWrap: "wrap",
    gap: theme.spacing.sm,
  },
  paginationInfo: {
    color: theme.colors.dark[2],
    fontSize: theme.fontSizes.sm,
  },
  paginationControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
  },
  createButton: {
    fontWeight: 600,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: `${theme.spacing.xl} ${theme.spacing.md}`,
    minHeight: rem(250),
    textAlign: "center",
  },
  emptyTitle: {
    color: theme.colors.dark[0],
    fontWeight: 600,
    marginBottom: theme.spacing.xs,
  },
  emptyDescription: {
    color: theme.colors.dark[3],
    maxWidth: rem(300),
  },
  errorState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xl,
    textAlign: "center",
    color: theme.colors.rose[5],
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.cyan[9] + "20",
    borderRadius: theme.radius.md,
    marginBottom: theme.spacing.sm,
  },
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing.md,
    gap: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.dark[6]}`,
  },
}));

// Skeleton loader for table rows
const TableSkeleton = ({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) => {
  const { classes } = useStyles();

  return (
    <Box>
      {/* Header skeleton */}
      <Box
        className={classes.skeletonRow}
        style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
      >
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={i}
            height={16}
            width={i === 0 ? "25%" : "15%"}
            radius="sm"
          />
        ))}
      </Box>
      {/* Row skeletons */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <Box key={rowIdx} className={classes.skeletonRow}>
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton
              key={colIdx}
              height={20}
              width={colIdx === 0 ? "30%" : "15%"}
              radius="sm"
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// Empty state component
const EmptyState = ({
  onCreateClick,
  createButtonText,
}: {
  onCreateClick?: () => void;
  createButtonText?: string;
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.emptyState}>
      <IconInbox
        size={40}
        stroke={1.5}
        color="var(--mantine-color-dark-3)"
        style={{ marginBottom: "var(--mantine-spacing-md)", opacity: 0.6 }}
      />
      <Text className={classes.emptyTitle} size="lg">
        No data found
      </Text>
      <Text className={classes.emptyDescription} size="sm">
        There are no items to display. Create your first item to get started.
      </Text>
      {onCreateClick && createButtonText && (
        <Button
          mt="lg"
          leftSection={<IconPlus size={16} />}
          onClick={onCreateClick}
        >
          {createButtonText}
        </Button>
      )}
    </Box>
  );
};

// Error state component
const ErrorState = ({
  error,
  onRetry,
}: {
  error: string;
  onRetry?: () => void;
}) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.errorState}>
      <IconAlertTriangle
        size={30}
        stroke={1.5}
        style={{ marginBottom: "var(--mantine-spacing-md)", opacity: 0.8 }}
      />
      <Text size="lg" fw={600} mb="xs">
        Failed to load data
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        {error}
      </Text>
      {onRetry && (
        <Button
          variant="light"
          color="rose"
          leftSection={<IconRefresh size={16} />}
          onClick={onRetry}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
};

export const AutoDataTable = <
  U extends {
    edges: Array<T>;
    nodes: Array<T>;
    pageInfo: ResponsePageInfo;
    totalCount?: Maybe<Scalars["Float"]>;
  },
  T,
  REQUEST extends DocumentNode | TypedDocumentNode<T[] | U>,
>({
  query,
  queryVariables,
  deleteMutation,
  columns,
  itemPath,
  rowExpansion,
  createButtonText,
  linkClick,
  disableNavigation,
  idField = "id" as any,
  enableSelection = false,
  onSelectionChange,
  emptyStateTitle,
  emptyStateDescription,
  emptyState,
  ...props
}: Omit<DataTableProps<T>, "columns" | "rowExpansion"> & {
  query: DocumentNode | TypedDocumentNode<T[] | U> | TadaDocumentNode<T[] | U>;
  queryVariables?: Record<string, any>;
  deleteMutation?: DocumentNode;
  createButtonText?: string | false;
  linkClick?: (item: T) => void;
  disableNavigation?: boolean;
  idField?: keyof T;
  columns: {
    title: string;
    accessor: string;
    description?: string;
    rawElement?: (item: T, idx?: number) => ReactNode;
    width?: number | string;
  }[];
  itemPath?: string;
  rowExpansion?: (item: T, idx?: number) => ReactNode;
  enableSelection?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyState?: ReactNode;
}) => {
  const navigate = useNavigate();
  const { cx, classes } = useStyles();
  const clipboard = useClipboard();
  const [expandedRecordIds, setExpandedRecordIds] = useState<string[]>([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsCountPerPage, setItemCountPerPage] = useState(10);
  const [isDeleting, setIsDeleting] = useState(false);

  const { loading, data, error, refetch } = useQuery(query, {
    fetchPolicy: "network-only",
    variables: {
      count: itemsCountPerPage,
      cursor: null,
      ...queryVariables,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [mappedColumns, setMappedColumns] = useState<Array<DataTableColumn<T>>>(
    [],
  );
  const [deleteItemMutation] = useMutation(
    deleteMutation ??
      gql`
        mutation noOp {
          __typename
        }
      `,
  );

  // Handle selection change
  useEffect(() => {
    onSelectionChange?.(selectedRecordIds);
  }, [selectedRecordIds, onSelectionChange]);

  useEffect(() => {
    const baseColumns = columns.map(
      ({ rawElement, description, title, width, ...column }, rowIdx) => ({
        ...column,
        width,
        title: description ? (
          <Group gap={4}>
            {title}
            <Tooltip label={description} withArrow>
              <IconInfoCircle
                size={14}
                style={{ cursor: "help", opacity: 0.6 }}
              />
            </Tooltip>
          </Group>
        ) : (
          title
        ),
        render:
          (rawElement && ((item: T, idx: number) => rawElement?.(item, idx))) ||
          ((item: T) =>
            rowIdx === 0 &&
            ((rowExpansion && (
              <Group gap="xs">
                <IconChevronRight
                  size="0.9em"
                  className={cx(classes.expandIcon, {
                    [classes.expandIconRotated]: expandedRecordIds.includes(
                      item[idField] as any as string,
                    ),
                  })}
                />
                <Anchor
                  onClick={() => {
                    if (linkClick) {
                      linkClick(item);
                      return;
                    }
                    if (disableNavigation) return;
                    navigate((itemPath ? `${itemPath}/` : "") + item[idField]);
                  }}
                >{`${(item as any)[column.accessor]}`}</Anchor>
              </Group>
            )) || (
              <Group gap={6}>
                <Anchor
                  onClick={() => {
                    if (linkClick) {
                      linkClick(item);
                      return;
                    }
                    if (disableNavigation) return;
                    navigate((itemPath ? `${itemPath}/` : "") + item[idField]);
                  }}
                >{`${(item as any)[column.accessor]}`}</Anchor>
                <Tooltip label="Copy ID to clipboard" withArrow>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={(e) => {
                      e.stopPropagation();
                      clipboard.copy(item[idField]);
                      notifications.show({
                        title: "Copied",
                        message: "ID copied to clipboard",
                        icon: <IconCheck size={16} />,
                        color: "green",
                      });
                    }}
                  >
                    <IconCopy size={12} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ))) ||
          undefined,
      }),
    );

    // Add delete column if mutation provided
    const columnsWithDelete = deleteMutation
      ? [
          ...baseColumns,
          {
            accessor: "_actions",
            title: "",
            width: 50,
            render: (item: T) => (
              <Tooltip label="Delete" withArrow>
                <ActionIcon
                  color="rose"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(item[idField] as any as string);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]
      : baseColumns;

    setMappedColumns(columnsWithDelete);
  }, [columns, expandedRecordIds]);

  // Delete confirmation with Mantine modal
  const handleDeleteClick = useCallback(
    (id: string) => {
      modals.openConfirmModal({
        title: (
          <Group gap="sm">
            <ThemeIcon color="rose" variant="light" size="lg" radius="md">
              <IconTrash size={18} />
            </ThemeIcon>
            <Text fw={600}>Delete Item</Text>
          </Group>
        ),
        centered: true,
        children: (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Are you sure you want to delete this item? This action cannot be
              undone.
            </Text>
          </Stack>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "rose" },
        onConfirm: async () => {
          await deleteItem(id);
        },
      });
    },

    [deleteItemMutation],
  );

  const deleteItem = async (id: string) => {
    if (!deleteItemMutation) return;
    setIsDeleting(true);
    try {
      await deleteItemMutation({
        variables: { id },
      });
      notifications.show({
        title: "Success",
        message: "Item deleted successfully",
        color: "green",
        icon: <IconCheck size={16} />,
      });
      await refetch();
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to delete item",
        color: "rose",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = useCallback(() => {
    if (selectedRecordIds.length === 0) return;

    modals.openConfirmModal({
      title: (
        <Group gap="sm">
          <ThemeIcon color="rose" variant="light" size="lg" radius="md">
            <IconTrash size={18} />
          </ThemeIcon>
          <Text fw={600}>Delete {selectedRecordIds.length} Items</Text>
        </Group>
      ),
      centered: true,
      children: (
        <Text size="sm" c="dimmed">
          Are you sure you want to delete {selectedRecordIds.length} selected
          items? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete All", cancel: "Cancel" },
      confirmProps: { color: "rose" },
      onConfirm: async () => {
        for (const id of selectedRecordIds) {
          await deleteItem(id);
        }
        setSelectedRecordIds([]);
      },
    });
  }, [selectedRecordIds]);

  const handleCreateClick = useCallback(() => {
    if (disableNavigation) return;
    navigate(itemPath ? `${itemPath}/new` : "new");
  }, [disableNavigation, itemPath]);

  const elemName = (query.definitions[0] as any).selectionSet.selections[0].name
    .value;

  const records = (data as any)?.[elemName]?.nodes ?? (data as any)?.[elemName];
  const totalCount = (data as any)?.[elemName]?.totalCount ?? 0;
  const pageInfo = (data as any)?.[elemName]?.pageInfo;

  return (
    <Box className={classes.container}>
      {/* Header with create button */}
      <Box className={classes.header}>
        <Group gap="sm">
          {createButtonText !== false && (
            <Button
              className={classes.createButton}
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateClick}
            >
              {createButtonText ?? "Create New"}
            </Button>
          )}
        </Group>

        {/* Bulk actions */}
        {enableSelection && selectedRecordIds.length > 0 && (
          <Box className={classes.bulkActions}>
            <Checkbox
              checked={true}
              indeterminate={
                selectedRecordIds.length > 0 &&
                selectedRecordIds.length < (records?.length ?? 0)
              }
              onChange={() => setSelectedRecordIds([])}
              label={`${selectedRecordIds.length} selected`}
            />
            {deleteMutation && (
              <Button
                size="xs"
                color="rose"
                variant="light"
                leftSection={<IconTrash size={14} />}
                onClick={handleBulkDelete}
                loading={isDeleting}
              >
                Delete Selected
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Error state */}
      {error && (
        <Paper className={classes.tableWrapper}>
          <ErrorState error={error.message} onRetry={() => refetch()} />
        </Paper>
      )}

      {/* Table */}
      {!error && (
        <Paper className={classes.tableWrapper}>
          {loading && !data ? (
            <TableSkeleton rows={5} columns={columns.length} />
          ) : !records?.length ? (
            emptyState || (
              <EmptyState
                onCreateClick={
                  createButtonText !== false ? handleCreateClick : undefined
                }
                createButtonText={
                  createButtonText !== false
                    ? (createButtonText ?? "Create New")
                    : undefined
                }
              />
            )
          ) : (
            <>
              <MantineDataTable
                columns={mappedColumns}
                fetching={loading}
                records={records}
                rowClassName={() => classes.row}
                highlightOnHover
                verticalSpacing="sm"
                horizontalSpacing="md"
                {...(enableSelection && {
                  selectedRecords: records?.filter((r: any) =>
                    selectedRecordIds.includes(r[idField]),
                  ),
                  onSelectedRecordsChange: (selected: T[]) =>
                    setSelectedRecordIds(
                      selected.map((r) => r[idField] as any as string),
                    ),
                })}
                {...(rowExpansion && {
                  rowExpansion: {
                    allowMultiple: false,
                    expanded: {
                      recordIds: expandedRecordIds,
                      onRecordIdsChange: setExpandedRecordIds,
                    },
                    content: ({ record, index }) =>
                      rowExpansion(record as T, index),
                  },
                })}
                {...(props as any)}
              />

              {/* Pagination */}
              {totalCount > 0 && (
                <Box className={classes.pagination}>
                  <Group gap="xs">
                    <Text className={classes.paginationInfo}>
                      Rows per page:
                    </Text>
                    <Select
                      size="xs"
                      data={["10", "20", "50", "100"]}
                      value={itemsCountPerPage.toString()}
                      onChange={(value) => {
                        if (value) {
                          setItemCountPerPage(parseInt(value));
                          setCurrentPage(0);
                        }
                      }}
                      style={{ width: 70 }}
                    />
                  </Group>

                  <Text className={classes.paginationInfo}>
                    {currentPage * itemsCountPerPage + 1}-
                    {Math.min(
                      currentPage * itemsCountPerPage + (records?.length ?? 0),
                      totalCount,
                    )}{" "}
                    of {totalCount}
                  </Text>

                  <Box className={classes.paginationControls}>
                    <ActionIcon
                      variant="subtle"
                      disabled={currentPage === 0}
                      onClick={() => {
                        // Go to previous page - would need cursor-based back navigation
                        // For now, just reset to first page
                        setCurrentPage(0);
                        refetch({
                          cursor: null,
                          count: itemsCountPerPage,
                          ...queryVariables,
                        });
                      }}
                    >
                      <IconArrowLeft size={18} />
                    </ActionIcon>

                    <ActionIcon
                      variant="subtle"
                      disabled={!pageInfo?.hasNextPage}
                      onClick={() => {
                        refetch({
                          cursor: pageInfo?.endCursor,
                          count: itemsCountPerPage,
                          ...queryVariables,
                        }).then(() => setCurrentPage(currentPage + 1));
                      }}
                    >
                      <IconArrowRight size={18} />
                    </ActionIcon>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};
